import { useState, useCallback, useEffect, use } from "react";

import { UserContext } from "../contexts/UserContext";
import type {
  TrialPlan,
  StoredTrialPlan,
  PlanScenario,
  PlanContent,
  PlanMilestone,
  PlanVendor,
  BudgetLine,
  EnrollmentScenario,
} from "../views/TrialPlanning/types";

const STORAGE_KEY = "vivo_trial_plans";

/** Plan-identity fields; every other TrialPlan field is scenario content. */
const IDENTITY_KEYS = new Set([
  "id",
  "name",
  "indication",
  "phase",
  "createdAt",
  "updatedAt",
  "activeScenarioId",
  "scenarioSummaries",
]);

/** Legacy plans stored their content flat on the plan; wrap it into a single
 *  "Base plan" scenario so nothing is lost when the scenario model lands. */
export function migratePlan(raw: Record<string, unknown>): StoredTrialPlan {
  if (Array.isArray(raw.scenarios) && raw.scenarios.length > 0) {
    return raw as unknown as StoredTrialPlan;
  }
  const content: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!IDENTITY_KEYS.has(key)) content[key] = value;
  }
  const now = new Date().toISOString();
  const scenario: PlanScenario = {
    id: crypto.randomUUID(),
    name: "Base plan",
    createdAt: (raw.createdAt as string) ?? now,
    updatedAt: (raw.updatedAt as string) ?? now,
    sites: [],
    milestones: [],
    ...content,
  };
  return {
    id: raw.id as string,
    name: raw.name as string,
    indication: (raw.indication as string) ?? "",
    phase: (raw.phase as string) ?? "",
    createdAt: scenario.createdAt,
    updatedAt: scenario.updatedAt,
    scenarios: [scenario],
    activeScenarioId: scenario.id,
  };
}

function activeScenarioOf(plan: StoredTrialPlan): PlanScenario {
  return (
    plan.scenarios.find((s) => s.id === plan.activeScenarioId) ??
    plan.scenarios[0]
  );
}

/** Flatten identity + the open scenario's content into the view screens use. */
const SCENARIO_META_KEYS = new Set([
  "id",
  "name",
  "createdBy",
  "updatedBy",
  "createdAt",
  "updatedAt",
  "approvedBy",
  "approvedAt",
]);

export function resolvePlan(plan: StoredTrialPlan): TrialPlan {
  const scenario = activeScenarioOf(plan);
  const content = Object.fromEntries(
    Object.entries(scenario).filter(([key]) => !SCENARIO_META_KEYS.has(key)),
  ) as unknown as PlanContent;
  return {
    id: plan.id,
    name: plan.name,
    indication: plan.indication,
    phase: plan.phase,
    createdAt: plan.createdAt,
    updatedAt: plan.updatedAt,
    ...content,
    activeScenarioId: scenario.id,
    scenarioSummaries: plan.scenarios.map((s) => ({
      id: s.id,
      name: s.name,
      createdBy: s.createdBy,
      updatedBy: s.updatedBy,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
      approvedBy: s.approvedBy,
      approvedAt: s.approvedAt,
      siteCount: s.sites?.length ?? 0,
    })),
  };
}

function loadStored(): StoredTrialPlan[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>[]) : [];
    const migrated = parsed.map(migratePlan);
    // Persist a migration immediately: migratePlan mints scenario ids, and
    // they must be stable across reads or activeScenarioId dangles.
    if (
      parsed.some(
        (p) => !Array.isArray(p.scenarios) || p.scenarios.length === 0,
      )
    ) {
      persist(migrated);
    }
    return migrated;
  } catch {
    return [];
  }
}

/** Keeps sibling hook instances (top bar, scenarios page, screens) in sync:
 *  every persisted change notifies all mounted useTrialPlans instances. */
const PLANS_UPDATED_EVENT = "vivo-trial-plans-updated";

function persist(plans: StoredTrialPlan[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
  window.dispatchEvent(new Event(PLANS_UPDATED_EVENT));
}

function defaultMilestones(): PlanMilestone[] {
  const names = [
    "Protocol Finalized",
    "Regulatory Submission",
    "First Patient In",
    "Enrollment Complete",
    "Last Patient Last Visit",
    "Database Lock",
    "CSR Submission",
  ];
  return names.map((event_name) => ({
    id: crypto.randomUUID(),
    event_name,
    event_type: "Planned Date" as const,
    start_date: "",
    end_date: "",
  }));
}

function defaultVendors(): PlanVendor[] {
  return [
    {
      id: crypto.randomUUID(),
      name: "OmniScience",
      type: "Technology",
      services: "Clinical operations platform, RBQM, real-world data",
      status: "considering",
      contact: "",
      notes:
        "Vivo platform — Ask Vivo AI, site profiles, enrollment forecasting",
    },
  ];
}

function defaultBudget(): BudgetLine[] {
  const lines: Array<Omit<BudgetLine, "id">> = [
    // Site Costs
    {
      category: "Site Costs",
      lineItem: "Site startup & activation fee",
      unit: "per site",
      quantitySource: "sites",
      quantity: 0,
      unitCost: 0,
      notes: "",
    },
    {
      category: "Site Costs",
      lineItem: "IRB/EC submission",
      unit: "per site",
      quantitySource: "sites",
      quantity: 0,
      unitCost: 0,
      notes: "",
    },
    {
      category: "Site Costs",
      lineItem: "Site Initiation Visit",
      unit: "per site",
      quantitySource: "sites",
      quantity: 0,
      unitCost: 0,
      notes: "",
    },
    // Per Patient
    {
      category: "Per Patient",
      lineItem: "Screening assessment",
      unit: "per screened",
      quantitySource: "screened",
      quantity: 0,
      unitCost: 0,
      notes: "",
    },
    {
      category: "Per Patient",
      lineItem: "Treatment visits",
      unit: "per enrolled",
      quantitySource: "enrolled",
      quantity: 0,
      unitCost: 0,
      notes: "",
    },
    {
      category: "Per Patient",
      lineItem: "Follow-up assessments",
      unit: "per enrolled",
      quantitySource: "enrolled",
      quantity: 0,
      unitCost: 0,
      notes: "",
    },
    {
      category: "Per Patient",
      lineItem: "Patient stipend & travel",
      unit: "per enrolled",
      quantitySource: "enrolled",
      quantity: 0,
      unitCost: 0,
      notes: "",
    },
    // Monitoring
    {
      category: "Monitoring",
      lineItem: "On-site monitoring visits",
      unit: "flat — set count",
      quantitySource: "manual",
      quantity: 0,
      unitCost: 0,
      notes: "~2-4 visits per site per year",
    },
    // CRO Fees
    {
      category: "CRO Fees",
      lineItem: "Project management",
      unit: "flat fee",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "",
    },
    {
      category: "CRO Fees",
      lineItem: "Medical monitoring",
      unit: "flat fee",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "",
    },
    // Data Management
    {
      category: "Data Management",
      lineItem: "EDC setup & license",
      unit: "flat fee",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "",
    },
    {
      category: "Data Management",
      lineItem: "Data cleaning & reconciliation",
      unit: "flat fee",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "",
    },
    // Regulatory
    {
      category: "Regulatory",
      lineItem: "IND submission & maintenance",
      unit: "flat fee",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "",
    },
    // Medical Writing
    {
      category: "Medical Writing",
      lineItem: "Study protocol & amendments",
      unit: "flat fee",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "",
    },
    {
      category: "Medical Writing",
      lineItem: "Clinical Study Report",
      unit: "flat fee",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "",
    },
    // Drug Supply
    {
      category: "Drug Supply",
      lineItem: "IP manufacturing & packaging",
      unit: "flat fee",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "",
    },
    {
      category: "Drug Supply",
      lineItem: "IP distribution & depot",
      unit: "flat fee",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "",
    },
    // Contingency
    {
      category: "Contingency",
      lineItem: "Reserve (10%)",
      unit: "flat",
      quantitySource: "manual",
      quantity: 1,
      unitCost: 0,
      notes: "Set unit cost to 10% of subtotal above",
    },
  ];
  return lines.map((l) => ({ ...l, id: crypto.randomUUID() }));
}

function defaultScenarios(): EnrollmentScenario[] {
  return [
    {
      id: crypto.randomUUID(),
      name: "Optimistic",
      rateMultiplier: 1.25,
      activationLagMultiplier: 0.75,
    },
    {
      id: crypto.randomUUID(),
      name: "Base Case",
      rateMultiplier: 1.0,
      activationLagMultiplier: 1.0,
    },
    {
      id: crypto.randomUUID(),
      name: "Pessimistic",
      rateMultiplier: 0.7,
      activationLagMultiplier: 1.4,
    },
  ];
}

function defaultContent() {
  return {
    sites: [],
    milestones: defaultMilestones(),
    vendors: defaultVendors(),
    protocols: [],
    budget: defaultBudget(),
    enrollmentScenarios: defaultScenarios(),
    overview: "",
    objective: "",
    primaryEndpoint: "",
    inclusionCriteria: "",
    exclusionCriteria: "",
    targetEnrollment: 0,
    screenFailureRate: 20,
    rampUpMonths: 2,
  };
}

export function useTrialPlans() {
  const [stored, setStored] = useState<StoredTrialPlan[]>(() => loadStored());

  // Optional context read: audit stamping degrades gracefully (undefined
  // editor) when no UserProvider is mounted, e.g. in unit tests.
  const userCtx = use(UserContext);
  const editorName =
    [userCtx?.userInfo?.first_name, userCtx?.userInfo?.last_name]
      .filter(Boolean)
      .join(" ") || undefined;

  const commit = useCallback((updated: StoredTrialPlan[]) => {
    persist(updated);
    setStored(updated);
  }, []);

  useEffect(() => {
    const onPlansUpdated = () => setStored(loadStored());
    window.addEventListener(PLANS_UPDATED_EVENT, onPlansUpdated);
    return () =>
      window.removeEventListener(PLANS_UPDATED_EVENT, onPlansUpdated);
  }, []);

  const refresh = useCallback(() => {
    setStored(loadStored());
  }, []);

  const createPlan = useCallback(
    (
      fields: Pick<TrialPlan, "name" | "indication" | "phase">,
      createdBy?: string,
    ): TrialPlan => {
      const now = new Date().toISOString();
      const scenario: PlanScenario = {
        id: crypto.randomUUID(),
        name: "Base plan",
        createdBy,
        createdAt: now,
        updatedAt: now,
        ...defaultContent(),
      };
      const newPlan: StoredTrialPlan = {
        ...fields,
        id: crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
        scenarios: [scenario],
        activeScenarioId: scenario.id,
      };
      commit([...loadStored(), newPlan]);
      return resolvePlan(newPlan);
    },
    [commit],
  );

  /** Identity changes land on the plan; everything else lands on the open
   *  scenario — edits apply to the open scenario automatically. */
  const updatePlan = useCallback(
    (id: string, changes: Partial<TrialPlan>): void => {
      const now = new Date().toISOString();
      const identity: Record<string, unknown> = {};
      const content: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(changes)) {
        (IDENTITY_KEYS.has(key) ? identity : content)[key] = value;
      }
      commit(
        loadStored().map((p) => {
          if (p.id !== id) return p;
          return {
            ...p,
            ...identity,
            updatedAt: now,
            scenarios: p.scenarios.map((s) =>
              s.id === p.activeScenarioId && Object.keys(content).length
                ? { ...s, ...content, updatedAt: now, updatedBy: editorName }
                : s,
            ),
          };
        }),
      );
    },
    [commit, editorName],
  );

  const deletePlan = useCallback(
    (id: string): void => {
      commit(loadStored().filter((p) => p.id !== id));
    },
    [commit],
  );

  const duplicatePlans = useCallback(
    (ids: string[]): void => {
      const current = loadStored();
      const now = new Date().toISOString();
      const copies = ids.flatMap((id) => {
        const src = current.find((p) => p.id === id);
        if (!src) return [];
        return [
          {
            ...structuredClone(src),
            id: crypto.randomUUID(),
            name: `${src.name} (copy)`,
            createdAt: now,
            updatedAt: now,
          },
        ];
      });
      commit([...current, ...copies]);
    },
    [commit],
  );

  const getPlan = useCallback(
    (id: string): TrialPlan | undefined => {
      const plan = stored.find((p) => p.id === id);
      return plan ? resolvePlan(plan) : undefined;
    },
    [stored],
  );

  // ---- scenarios: the entire plan at a point in time ------------------------

  const setActiveScenario = useCallback(
    (planId: string, scenarioId: string): void => {
      commit(
        loadStored().map((p) =>
          p.id === planId && p.scenarios.some((s) => s.id === scenarioId)
            ? { ...p, activeScenarioId: scenarioId }
            : p,
        ),
      );
    },
    [commit],
  );

  /** New scenario — blank by default, or a full copy of `fromScenarioId`. The
   *  new scenario becomes the open one. */
  const createScenario = useCallback(
    (
      planId: string,
      name: string,
      fromScenarioId?: string,
      createdBy?: string,
    ): void => {
      const now = new Date().toISOString();
      commit(
        loadStored().map((p) => {
          if (p.id !== planId) return p;
          const src = fromScenarioId
            ? p.scenarios.find((s) => s.id === fromScenarioId)
            : undefined;
          const scenario: PlanScenario = {
            ...(src ? structuredClone(src) : defaultContent()),
            id: crypto.randomUUID(),
            name,
            createdBy: createdBy ?? editorName,
            updatedBy: undefined,
            approvedBy: undefined,
            approvedAt: undefined,
            createdAt: now,
            updatedAt: now,
          } as PlanScenario;
          return {
            ...p,
            scenarios: [...p.scenarios, scenario],
            activeScenarioId: scenario.id,
            updatedAt: now,
          };
        }),
      );
    },
    [commit, editorName],
  );

  const renameScenario = useCallback(
    (planId: string, scenarioId: string, name: string): void => {
      const now = new Date().toISOString();
      commit(
        loadStored().map((p) =>
          p.id === planId
            ? {
                ...p,
                scenarios: p.scenarios.map((s) =>
                  s.id === scenarioId
                    ? { ...s, name, updatedAt: now, updatedBy: editorName }
                    : s,
                ),
              }
            : p,
        ),
      );
    },
    [commit, editorName],
  );

  /** Approval gates the Add to Vivo publish flow; it is a marker, not (yet)
   *  a hard content lock. Only one scenario per plan can be approved at a
   *  time — approving one clears approval on the others. */
  const setScenarioApproval = useCallback(
    (planId: string, scenarioId: string, approved: boolean): void => {
      const now = new Date().toISOString();
      commit(
        loadStored().map((p) =>
          p.id === planId
            ? {
                ...p,
                scenarios: p.scenarios.map((s) => {
                  if (s.id === scenarioId) {
                    return {
                      ...s,
                      approvedAt: approved ? now : undefined,
                      approvedBy: approved ? editorName : undefined,
                    };
                  }
                  return approved
                    ? { ...s, approvedAt: undefined, approvedBy: undefined }
                    : s;
                }),
              }
            : p,
        ),
      );
    },
    [commit, editorName],
  );

  /** The last scenario cannot be deleted — a plan always has one. */
  const deleteScenario = useCallback(
    (planId: string, scenarioId: string): void => {
      commit(
        loadStored().map((p) => {
          if (p.id !== planId || p.scenarios.length <= 1) return p;
          const scenarios = p.scenarios.filter((s) => s.id !== scenarioId);
          return {
            ...p,
            scenarios,
            activeScenarioId:
              p.activeScenarioId === scenarioId
                ? scenarios[0].id
                : p.activeScenarioId,
          };
        }),
      );
    },
    [commit],
  );

  return {
    plans: stored.map(resolvePlan),
    refresh,
    createPlan,
    updatePlan,
    deletePlan,
    duplicatePlans,
    getPlan,
    setActiveScenario,
    createScenario,
    renameScenario,
    deleteScenario,
    setScenarioApproval,
  };
}
